import { useEffect, useState, useRef, useCallback } from "react";
import _merge from "lodash/merge";
import { Rothko, RothkoOptions } from "web/script/modules/rothko";
import { URLQuery } from "web/script/utils/url";

export enum LOADING_STATES {
    DEFAULT,
    PENDING,
    COMPLETE,
    ERROR,
}

export interface Options {
    enabled?: boolean;
    rothko?: RothkoOptions;
}

const defaultOptions: Options = {
    enabled: true,
};

interface UseRothkoResult<TData = unknown> {
    data: TData | undefined;
    loadingState: LOADING_STATES;
    isFetching: boolean;
    isError: boolean;
    isFetched: boolean;
    isSuccess: boolean;
    run(): void;
}

/*  Note - parameters needs to be a reference to an object between re-renders as useEffect does not do a deep object comparison when deciding
if it should execute. For example:
```
const rothkoOptions = useMemo(() =>({ slug: productSlug, uid: productUid }), [productSlug, productUid]);
const { data, loadingState } = useRothko(endpoint, rothkoOptions);
```
This will ensure parameters is always a reference to the same object
*/
export function useRothko<TData = unknown>(
    endpoint: string,
    parameters?: URLQuery,
    options?: Options
): UseRothkoResult<TData> {
    const clientRef = useRef<Rothko | undefined>(undefined);
    const [data, setData] = useState<TData | undefined>(undefined);
    const [loadingState, setLoadingState] = useState(LOADING_STATES.DEFAULT);
    const { enabled } = _merge({}, defaultOptions, options || {});

    const run = useCallback(() => {
        setLoadingState(LOADING_STATES.PENDING);
        clientRef.current?.fetch(parameters).catch(() => setLoadingState(LOADING_STATES.ERROR));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(parameters)]);

    useEffect(() => {
        const client = new Rothko(endpoint, options?.rothko);
        client.on("data", (ev, response) => {
            setData(response);
            setLoadingState(LOADING_STATES.COMPLETE);
        });
        clientRef.current = client;
    }, [endpoint, options]);

    useEffect(() => {
        if (enabled) {
            run();
        }
    }, [enabled, run]);

    return {
        data,
        isFetching:
            loadingState === LOADING_STATES.PENDING || loadingState === LOADING_STATES.DEFAULT,
        isError: loadingState === LOADING_STATES.ERROR,
        isFetched:
            loadingState === LOADING_STATES.COMPLETE || loadingState === LOADING_STATES.ERROR,
        isSuccess: loadingState === LOADING_STATES.COMPLETE,
        loadingState,
        run,
    };
}
